import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export type AmbientTrack = 'eternal-twilight' | 'tavern' | 'dungeon-crawl' | 'encyclopedia' | 'none'

interface AudioState {
  muted: boolean
  musicVolume: number // 0..1
  sfxVolume: number // 0..1
  currentTrack: AmbientTrack
  setMuted: (v: boolean) => void
  toggleMuted: () => void
  setMusicVolume: (v: number) => void
  setSfxVolume: (v: number) => void
  setTrack: (t: AmbientTrack) => void
}

/**
 * Volume/mute persistence for the immersive audio layer.
 * The actual Howler playback engine lives in src/features/audio and reads
 * from this store. See Style Guide §"Music & Audio".
 */
export const useAudioStore = create<AudioState>()(
  persist(
    (set) => ({
      muted: true, // default off — user opts in (autoplay-friendly + accessible)
      musicVolume: 0.4,
      sfxVolume: 0.6,
      currentTrack: 'eternal-twilight',
      setMuted: (v) => set({ muted: v }),
      toggleMuted: () => set((s) => ({ muted: !s.muted })),
      setMusicVolume: (v) => set({ musicVolume: clamp(v) }),
      setSfxVolume: (v) => set({ sfxVolume: clamp(v) }),
      setTrack: (t) => set({ currentTrack: t }),
    }),
    { name: 'grymwyrd-audio' },
  ),
)

const clamp = (v: number) => Math.min(1, Math.max(0, v))
